import { Badge } from '../ui/badge'
import {
  Server,
  MessageSquare,
  Gift,
  Globe,
  RefreshCw,
  Calendar,
  Coins,
  Zap,
  Key,
  Bot,
} from 'lucide-react'

export interface Transaction {
  id: number
  transaction_type: string | null
  credits_amount: number
  metadata: Record<string, unknown> | null
  created_at: string
  user_email: string | null
  creditable_type: string | null
  creditable_id: number | null
  creditable_name?: string | null
}

type TFunction = (key: string, options?: Record<string, unknown>) => string

const TYPE_CONFIG: Record<string, { labelKey: string; className: string; icon: React.ReactNode }> = {
  live_hosting: {
    labelKey: 'creditTransactions.types.live_hosting',
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
    icon: <Server className="h-3 w-3 mr-1" />,
  },
  ai_message: {
    labelKey: 'creditTransactions.types.ai_message',
    className: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
    icon: <MessageSquare className="h-3 w-3 mr-1" />,
  },
  admin_grant: {
    labelKey: 'creditTransactions.types.admin_grant',
    className: 'bg-green-100 text-green-800 hover:bg-green-200',
    icon: <Gift className="h-3 w-3 mr-1" />,
  },
  domain_purchase: {
    labelKey: 'creditTransactions.types.domain_purchase',
    className: 'bg-orange-100 text-orange-800 hover:bg-orange-200',
    icon: <Globe className="h-3 w-3 mr-1" />,
  },
  domain_refund: {
    labelKey: 'creditTransactions.types.domain_refund',
    className: 'bg-teal-100 text-teal-800 hover:bg-teal-200',
    icon: <RefreshCw className="h-3 w-3 mr-1" />,
  },
  monthly_reset: {
    labelKey: 'creditTransactions.types.monthly_reset',
    className: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
    icon: <Calendar className="h-3 w-3 mr-1" />,
  },
  credit_purchase: {
    labelKey: 'creditTransactions.types.credit_purchase',
    className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
    icon: <Coins className="h-3 w-3 mr-1" />,
  },
  sandbox_hosting: {
    labelKey: 'creditTransactions.types.sandbox_hosting',
    className: 'bg-slate-100 text-slate-800 hover:bg-slate-200',
    icon: <Server className="h-3 w-3 mr-1" />,
  },
  always_on: {
    labelKey: 'creditTransactions.types.always_on',
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-200',
    icon: <Zap className="h-3 w-3 mr-1" />,
  },
  ai_api_proxy: {
    labelKey: 'creditTransactions.types.ai_api_proxy',
    className: 'bg-violet-100 text-violet-800 hover:bg-violet-200',
    icon: <MessageSquare className="h-3 w-3 mr-1" />,
  },
  access_code_redemption: {
    labelKey: 'creditTransactions.types.access_code_redemption',
    className: 'bg-lime-100 text-lime-800 hover:bg-lime-200',
    icon: <Key className="h-3 w-3 mr-1" />,
  },
  access_code_expiry: {
    labelKey: 'creditTransactions.types.access_code_expiry',
    className: 'bg-red-100 text-red-800 hover:bg-red-200',
    icon: <Key className="h-3 w-3 mr-1" />,
  },
  managed_agent: {
    labelKey: 'creditTransactions.types.managed_agent',
    className: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200',
    icon: <Bot className="h-3 w-3 mr-1" />,
  },
}

export function getTypeBadge(type: string | null, t: TFunction) {
  const c = type ? (TYPE_CONFIG[type] || { labelKey: '', className: 'bg-gray-100 text-gray-800', icon: null }) : null
  return (
    <Badge variant="secondary" className={c?.className ?? 'bg-gray-100 text-gray-800'}>
      {c?.icon}
      {c?.labelKey ? t(c.labelKey) : (type || '—')}
    </Badge>
  )
}

export function getDetails(transaction: Transaction, t: TFunction): string {
  const m = transaction.metadata || {}

  switch (transaction.transaction_type ?? '') {
    case 'live_hosting':
      return String(m.project_name || m.fly_app_name || '—')
    case 'ai_message':
      return t('creditTransactions.details.conversationId', { id: m.conversation_id || '—' })
    case 'admin_grant':
      return String(m.reason || m.granted_by_email || t('creditTransactions.details.manualGrant'))
    case 'domain_purchase':
    case 'domain_refund':
      return String(m.domain_name || '—')
    case 'monthly_reset':
      return t('creditTransactions.details.rollover', { amount: m.rollover_amount || 0 })
    case 'credit_purchase':
      return t('creditTransactions.details.creditPurchase', { price: m.price_usd || 0 })
    case 'sandbox_hosting': {
      const key = m.charge_type === 'final' ? 'creditTransactions.details.sandboxHostingFinal' : 'creditTransactions.details.sandboxHosting'
      return t(key, { hours: m.hours || 0, size: m.instance_size || '—', project: m.project_name || '—' })
    }
    case 'always_on':
      return t('creditTransactions.details.alwaysOn', { project: m.project_name || '—' })
    case 'ai_api_proxy':
      return t('creditTransactions.details.aiApiProxy', { model: m.model || '—', tokens_in: m.tokens_in || 0, tokens_out: m.tokens_out || 0 })
    case 'access_code_redemption':
    case 'access_code_expiry':
      return String(m.code || m.label || '—')
    case 'managed_agent':
      return t('creditTransactions.details.managedAgent', { model: m.model || '—', cost: m.cost_usd || 0 })
    default:
      return '—'
  }
}

export const ALL_TRANSACTION_TYPES = Object.keys(TYPE_CONFIG)
